import {
    DEFAULT_PIE_CHART_DATA_LABEL_PROPS,
    PieChartSeries,
    PiePlotOptions,
    PieChart,
} from "~/modules/visualization/components/PieChart";
import { Asset } from "~/modules/assets/types";
import React from "react";
import { getAssetPieChartSeries } from "./getAssetPieChartSeries";
import { AssetPieChartGroupBy } from "./types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartPieSimpleCircleDollar } from "@fortawesome/pro-regular-svg-icons";

// // // //

function AssetPieChartLayout(props: {
    assets: Asset[];
    groupBy: AssetPieChartGroupBy;
    height?: number;
    showLabels?: boolean;
    animation?: boolean;
    measure?:
        | "market_value_dollar"
        | "total_asset_value_dollar"
        | "maximum_capacity_dollar"
        | "available_capacity_dollar"
        | "circulating_market_value_dollar"
        | "circulating_asset_value_dollar"
        | "bridged_token_value_dollar"
        | "bridged_token_market_cap_dollar"
        | "outstanding_capital_dollar";
    modifySeries?: (
        series: PieChartSeries,
        groupBy: AssetPieChartGroupBy,
    ) => PieChartSeries;
    additionalSeries?: PieChartSeries[];
}) {
    const {
        assets,
        groupBy,
        height,
        showLabels = true,
        measure = "market_value_dollar",
        animation = true,
        modifySeries = null,
        additionalSeries = [],
    } = props;

    // Build PieChartSeries[] for PieChart
    const series = getAssetPieChartSeries({
        assets,
        groupBy,
        measure,
    })
        .map((s) => {
            const series = {
                id: s.name,
                name: s.name,
                slug: s.slug,
                value: s.value,
                color: s.color,
            };

            if (modifySeries) {
                return modifySeries(series, groupBy);
            }

            return series;
        })
        .filter((s) => {
            return s.value > 0;
        });

    // Define props.plotOptions for PieChart
    // ENHANCEMENT - configure this via props
    const plotOptions: PiePlotOptions = {
        dataLabels: {
            ...DEFAULT_PIE_CHART_DATA_LABEL_PROPS,
            enabled: showLabels,
            allowOverlap: true,
            minSize: 0,
            filter: {
                property: "percentage",
                operator: ">",
                value: 0,
            },
        },
    };

    if (series.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-[300px] gap-3 text-muted-foreground">
                <FontAwesomeIcon
                    icon={faChartPieSimpleCircleDollar}
                    className="h-8 w-8"
                />
                <p className="text-sm font-light">
                    No data available for this selection
                </p>
            </div>
        );
    }

    return (
        <PieChart
            series={[...series, ...additionalSeries]}
            plotOptions={plotOptions}
            height={height}
            animation={animation}
        />
    );
}

export const AssetPieChart = React.memo(AssetPieChartLayout);
